import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Info, Loader2, MinusCircle, Trash2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";

const PROJECT_ROLES = ["PROJECT_MANAGER", "ENGINEERING", "PROCUREMENT", "SITE", "VIEWER"];
const LEVEL_LABEL = { NONE: "None", VIEW: "View", EDIT: "Edit", APPROVE: "Approve", CUSTOM: "Custom" };
const LEVEL_TONE = { NONE: "text-muted-foreground", VIEW: "text-sky-600 dark:text-sky-400", EDIT: "text-emerald-600 dark:text-emerald-400", APPROVE: "font-semibold text-[#910029]", CUSTOM: "text-amber-600 dark:text-amber-400" };
const ACTIONS = [["view", "View"], ["create", "Create"], ["edit", "Edit"], ["delete", "Delete (own draft)"], ["approve", "Approve"], ["certify", "Certify"], ["post", "Post"], ["cancel", "Cancel"], ["delete_any", "Delete anything"]];
const STEP_ICON = { pass: CheckCircle2, fail: XCircle, none: MinusCircle, skip: MinusCircle };
const STEP_TONE = { pass: "text-emerald-600 dark:text-emerald-400", fail: "text-rose-600 dark:text-rose-400", none: "text-muted-foreground", skip: "text-muted-foreground" };

/** One person's access: role, projects (editable), what they can do, and "why can't they?". */
export default function UserAccessPage() {
  const { userId } = useParams();
  const [info, setInfo] = useState(null);
  const [matrix, setMatrix] = useState(null);       // module list for the explainer
  const [allProjects, setAllProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: m }, { data: p }, { data: users }] = await Promise.all([
        api.get(`/rbac/users/${userId}/access`), api.get("/rbac/matrix"), api.get("/projects").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setInfo(data); setMatrix(m); setAllProjects(p || []); setPeople((users || []).filter((x) => x.is_active)); setFailed(false);
    } catch (err) {
      setFailed(true);
      toast.error(getErrorMessage(err));
    }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (failed) return <p className="text-sm text-muted-foreground">Could not load this person. <Link className="underline" to="/erp/governance/users">Back to Users</Link></p>;
  if (!info || !matrix) return <p className="text-sm text-muted-foreground" data-testid="user-access-loading">Loading…</p>;
  const u = info.user;

  return (
    <div className="space-y-6" data-testid="user-access-page">
      <Link to="/erp/governance/users" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3 w-3" /> Users</Link>
      <PageHeader title={u.full_name} description={`${u.email} · ${u.role_label}${u.is_active ? "" : " · inactive"}. Change their role or details on the Users page.`} />
      <div className="grid gap-6 lg:grid-cols-2">
        <ProjectsCard info={info} allProjects={allProjects} onSaved={load} />
        <CanDoCard info={info} matrix={matrix} />
      </div>
      <WhyCard info={info} matrix={matrix} allProjects={allProjects} />
    </div>
  );
}

function ProjectsCard({ info, allProjects, onSaved }) {
  const u = info.user;
  const byRole = ["ORG_ADMIN", "PORTFOLIO_LEAD"].includes(u.role);          // these roles see every project whatever is ticked
  const [all, setAll] = useState(Boolean(u.all_projects));
  const [access, setAccess] = useState(Object.fromEntries(info.projects.map((p) => [p.project_id, p.role])));
  const [saving, setSaving] = useState(false);

  const toggle = (id, on) => setAccess((a) => { const next = { ...a }; if (on) next[id] = next[id] || "VIEWER"; else delete next[id]; return next; });
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${u.id}`, { all_projects: all, project_access: Object.entries(access).map(([project_id, role]) => ({ project_id, role })) });
      toast.success("Project access saved");
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4" data-testid="user-projects-card">
      <h2 className="text-sm font-semibold text-foreground">Projects</h2>
      {byRole ? (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="user-sees-all-by-role">{u.role_label}s see every project, so there is nothing to choose here.</p>
      ) : (
        <>
          <label className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
            <span>Can see all projects<span className="block text-[11px] text-muted-foreground">Includes projects added later.</span></span>
            <Switch checked={all} onCheckedChange={setAll} data-testid="user-all-projects" />
          </label>
          {!all && (
            <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto" data-testid="user-project-list">
              {allProjects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet.</p>}
              {allProjects.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-1.5">
                  <Checkbox checked={p.id in access} onCheckedChange={(v) => toggle(p.id, Boolean(v))} data-testid={`user-project-${p.code}`} />
                  <span className="flex-1 truncate text-sm">{p.code} — {p.name}</span>
                  {p.id in access && (
                    <Select value={access[p.id]} onValueChange={(v) => setAccess({ ...access, [p.id]: v })}>
                      <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROJECT_ROLES.map((r) => <SelectItem key={r} value={r}>{r.replaceAll("_", " ").toLowerCase()}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>
          )}
          <Button className="mt-3" onClick={save} disabled={saving} data-testid="user-projects-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save projects</Button>
        </>
      )}
    </section>
  );
}

function CanDoCard({ info, matrix }) {
  const mods = matrix.modules.filter((m) => !m.parent);
  return (
    <section className="rounded-xl border border-border bg-card p-4" data-testid="user-can-do-card">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">What they can do</h2>
        <Link to="/erp/governance/access" className="text-xs text-muted-foreground underline hover:text-foreground">Change {info.user.role_label} access</Link>
      </div>
      <ul className="mt-3 divide-y divide-border/60 text-sm">
        {mods.map((m) => {
          const a = info.access[m.module];
          return (
            <li key={m.module} className="flex items-center justify-between py-1.5" data-testid={`user-level-${m.module}`}>
              <span>{m.label}</span>
              <span className={`text-xs ${LEVEL_TONE[a.level]}`}>
                {LEVEL_LABEL[a.level]}
                {a.approval_limit !== null && <span className="ml-1 text-muted-foreground">up to ₹{Number(a.approval_limit).toLocaleString("en-IN")}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function WhyCard({ info, matrix, allProjects }) {
  const [form, setForm] = useState({ module: "", action: "view", project_id: "", amount: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = async (e) => {
    e.preventDefault();
    if (!form.module) return;
    setBusy(true);
    try {
      const { data } = await api.post("/rbac/explain", {
        user_id: info.user.id, module: form.module, action: form.action,
        project_id: form.project_id || null, amount: form.amount === "" ? null : form.amount,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4" data-testid="why-card">
      <h2 className="text-sm font-semibold text-foreground">Why can't they?</h2>
      <p className="mt-1 text-xs text-muted-foreground">Pick what {info.user.full_name} is trying to do. You get the answer and each check that led to it.</p>
      <form onSubmit={ask} className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Label>Module</Label>
          <Select value={form.module} onValueChange={(v) => setForm({ ...form, module: v })}>
            <SelectTrigger className="mt-1.5" data-testid="why-module"><SelectValue placeholder="Choose a module" /></SelectTrigger>
            <SelectContent>{matrix.modules.map((m) => <SelectItem key={m.module} value={m.module}>{m.parent ? "  " : ""}{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Action</Label>
          <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
            <SelectTrigger className="mt-1.5" data-testid="why-action"><SelectValue /></SelectTrigger>
            <SelectContent>{ACTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Project <span className="font-normal text-muted-foreground">(optional)</span></Label>
          <Select value={form.project_id || "any"} onValueChange={(v) => setForm({ ...form, project_id: v === "any" ? "" : v })}>
            <SelectTrigger className="mt-1.5" data-testid="why-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Not for a project</SelectItem>
              {allProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Amount (₹) <span className="font-normal text-muted-foreground">(optional)</span></Label>
          <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="why-amount" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Button type="submit" disabled={busy || !form.module} data-testid="why-ask">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Check</Button>
        </div>
      </form>

      {result && (
        <div className="mt-4 space-y-3" data-testid="why-result">
          <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${result.allowed ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-rose-500/10 text-rose-700 dark:text-rose-400"}`} data-testid="why-verdict">
            {result.allowed ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <XCircle className="mt-0.5 h-4 w-4" />}
            <span><b>{result.allowed ? "They can." : "They can't."}</b> {result.reason}{result.via ? ` (through ${result.via})` : ""}</span>
          </div>
          <ol className="space-y-1.5">
            {result.steps.map((s, i) => {
              const Icon = STEP_ICON[s.result] || Info;
              return (
                <li key={i} className="flex items-start gap-2 text-sm" data-testid={`why-step-${s.step}`}>
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${STEP_TONE[s.result]}`} />
                  <span><b className="font-medium">{s.step}.</b> <span className="text-muted-foreground">{s.detail}</span></span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}



/** Access for a limited time: a level on one module until a date, with a reason. The person's role is not changed. */
